import { Button, Typography } from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  type GridPaginationModel,
  type GridSortModel,
} from '@mui/x-data-grid';
import { groupBy } from 'lodash';
import { useContext, useState } from 'react';
import TenantContext from '../../lib/context/TenantContext';
import { useGetBatchHistory } from '../../lib/query/batch';
import { columns } from './gridColumns';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  CurrentProcess,
  FileExtractionStatus,
  stringToCurrentProcess,
  stringToFileExtractionStatus,
} from '../../models/jobStatus';

export interface IGridData {
  PartitionKey: string;
  RowKey: string;
  BatchId: string;
  FileCount: number;
  InvoiceDate: string;
  Author: string;
  Files: { fileName: string; status: string }[];
  ExportDate: Date;
  Status: 'processing' | 'partial' | 'success' | 'failed';
  CreatedBy: string;
  PostToTally?: boolean;
  TallyResponse?: string;
  tallyMessages?: string[];
  IsEmptySheet?: boolean;
}

const BatchHistoryPage = () => {
  const { tenant } = useContext(TenantContext);
  const { data, isFetching, refetch } = useGetBatchHistory(tenant.id);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'ExportDate', sort: 'desc' },
  ]);

  const onPaginationModelChange = (model: GridPaginationModel) => {
    setPage(model.page);
    setRowsPerPage(model.pageSize);
  };

  const formatData = () => {
    if (data) {
      const groupedData = groupBy(data, item => item.BatchId);
      const result = Object.entries(groupedData).map(item => {
        let status: string = 'processing';
        const batchItem = item[1].find(e => e.RowKey === item[0]);
        const jobStatus = stringToCurrentProcess(batchItem?.JobStatus ?? '');

        const allFilesStatus = item[1]
          .filter(e => e.RowKey !== item[0])
          .map(e => stringToFileExtractionStatus(e.FileStatus));

        const didAllSucceed = allFilesStatus.every(
          status => status === FileExtractionStatus.ExtractionCompleted,
        );

        const didAnyFail = allFilesStatus.some(
          status => status === FileExtractionStatus.ExtractionFailure,
        );

        if (
          didAllSucceed &&
          jobStatus === CurrentProcess.MergeActivityComplete
        ) {
          status = 'success';
        } else if (
          didAllSucceed &&
          jobStatus === CurrentProcess.MergeActivityFailed
        ) {
          status = 'failed';
        } else if (
          didAnyFail &&
          jobStatus === CurrentProcess.MergeActivityComplete
        ) {
          status = 'partial';
        } else if (didAnyFail) {
          status = 'failed';
        }

        return {
          BatchId: item[0],
          FileCount: item[1].length - 1,
          CreatedBy: item[1][0].CreatedBy,
          Files: item[1].map(file => ({
            fileName: file.FileName,
            status: file.FileStatus,
          })),
          ExportDate: item[1][0].CreatedDate,
          Status: status,
          PartitionKey: tenant.id,
          RowKey: item[0],
          PostToTally: !!batchItem?.PostToTally,
          TallyResponse: batchItem?.TallyResponse,
        };
      });
      return result;
    } else return [];
  };

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer style={{ justifyContent: 'space-between' }}>
        <div className="flex justify-between w-full p-4">
          <div>
            <GridToolbarQuickFilter
              quickFilterParser={(searchInput: string) =>
                searchInput.split(',').map(value => value.trim())
              }
              debounceMs={200}
            />
          </div>
          <div>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              startIcon={<RefreshIcon />}
              onClick={async () => await refetch()}>
              Refresh
            </Button>
          </div>
        </div>
      </GridToolbarContainer>
    );
  };

  return (
    <div className="mt-4">
      <div className="mb-4">
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Batch History
        </Typography>
      </div>

      <DataGrid
        columns={columns(() => refetch().then(() => {}))}
        rows={formatData() ?? []}
        getRowId={row => row.BatchId}
        loading={isFetching}
        pageSizeOptions={[5, 10, 20]}
        paginationModel={{ page, pageSize: rowsPerPage }}
        onPaginationModelChange={onPaginationModelChange}
        slots={{
          toolbar: CustomToolbar,
        }}
        autoHeight
        disableColumnSorting={false}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        sx={{
          '& .MuiDataGrid-virtualScroller': {
            overflowX: 'auto',
          },
        }}
      />
    </div>
  );
};

export default BatchHistoryPage;
